import { notifications } from '@mantine/notifications';
import { IconFolderExclamation } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '../hooks/useAppDispatch';
import { useAppSelector } from '../hooks/useAppSelector';
import { useActiveProjectScope } from '../hooks/useActiveProject';
import { PROJECTS_QUERY_KEY, useProjectsQuery } from '../hooks/useProjectsQuery';
import { setProjectErrorHandler } from '../libs/axios';
import { setActiveProject } from '../store/project/project.actions';
import { activeProjectStorageKey, resolveActiveProjectId } from '../utils/projects';
import storage from '../utils/storage';

export default function ProjectProvider({ children }: PropsWithChildren) {
    const { t } = useTranslation('projects');
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const userId = useAppSelector((state) => state.auth.user?.id);
    const { activeProjectId, isResolved } = useActiveProjectScope();
    const { data: projects, isError } = useProjectsQuery();

    useEffect(() => {
        setProjectErrorHandler(() => {
            void queryClient.invalidateQueries(
                { queryKey: PROJECTS_QUERY_KEY },
                { cancelRefetch: false }
            );
        });
        return () => setProjectErrorHandler(undefined);
    }, [queryClient]);

    useEffect(() => {
        if (userId === undefined) return;

        if (!projects) {
            if (isError && !isResolved) {
                dispatch(setActiveProject({ userId, projectId: null }));
            }
            return;
        }

        const preferredId =
            isResolved && activeProjectId
                ? activeProjectId
                : storage.get<string | null>(activeProjectStorageKey(userId), null);
        const nextId = resolveActiveProjectId(projects, preferredId);

        if (isResolved && nextId === activeProjectId) return;

        if (preferredId && nextId !== preferredId) {
            notifications.show({
                title: t('stale.title'),
                message: t('stale.message', {
                    name: projects.find((project) => project.id === nextId)?.name ?? '',
                }),
                icon: <IconFolderExclamation size={20} color="#f59e0b" aria-hidden="true" />,
                color: 'yellow',
            });
        }

        dispatch(setActiveProject({ userId, projectId: nextId }));
    }, [userId, projects, isError, isResolved, activeProjectId, dispatch, t]);

    useEffect(() => {
        if (userId !== undefined && activeProjectId) {
            storage.set(activeProjectStorageKey(userId), activeProjectId);
        }
    }, [userId, activeProjectId]);

    return <>{children}</>;
}
